import { publicApiClient, privateApiClient } from '../apiClient';
import API from '../../config/api';

/**
 * Send an SMS notification
 * @param {string} phoneNumber - Phone number in international format (e.g., +966XXXXXXXXX)
 * @param {string} message - Message content
 * @param {boolean} isUnicode - Whether the message contains Unicode characters (e.g., Arabic)
 * @returns {Promise<Object>} - Response from the API
 */
export const sendSMS = async (phoneNumber, message, isUnicode = true) => {
  try {
    const response = await privateApiClient.post(API.notifications.sendSMS, {
      phone_number: phoneNumber,
      message: message,
      is_unicode: isUnicode
    });
    return response.data;
  } catch (error) {
    console.error('Error sending SMS:', error);
    throw error;
  }
};

/**
 * Send an OTP code to a phone number
 * @param {string} phoneNumber - Phone number in international format (e.g., +966XXXXXXXXX)
 * @returns {Promise<Object>} - Response from the API
 */
export const sendOTP = async (phoneNumber) => {
  try {
    const response = await publicApiClient.post(API.notifications.sendOTP, {
      phone_number: phoneNumber
    });
    return response.data;
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
};

/**
 * Verify an OTP code
 * @param {string} phoneNumber - Phone number in international format (e.g., +966XXXXXXXXX)
 * @param {string} otpCode - The OTP code to verify
 * @returns {Promise<Object>} - Response from the API
 */
export const verifyOTP = async (phoneNumber, otpCode) => {
  try {
    const response = await publicApiClient.post(API.notifications.verifyOTP, {
      phone_number: phoneNumber,
      otp_code: otpCode
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

/**
 * Convert a Saudi phone number to international format
 * @param {string} phoneNumber - Saudi phone number (e.g., 05XXXXXXXX or 5XXXXXXXX)
 * @returns {string} - Phone number in international format (e.g., +966XXXXXXXX)
 */
export const formatSaudiPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove any non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Handle different formats
  if (digitsOnly.startsWith('966')) {
    // Already in international format with country code
    return `+${digitsOnly}`;
  } else if (digitsOnly.startsWith('05')) {
    // Format: 05XXXXXXXX -> +9665XXXXXXXX
    return `+966${digitsOnly.substring(1)}`;
  } else if (digitsOnly.startsWith('5')) {
    // Format: 5XXXXXXXX -> +9665XXXXXXXX
    return `+966${digitsOnly}`;
  }
  
  // Default case: assume it's a local number without prefix
  return `+966${digitsOnly}`;
};

const smsService = {
  sendSMS,
  sendOTP,
  verifyOTP,
  formatSaudiPhoneNumber
};

export default smsService;
